import React, {useEffect, useState} from 'react';
import {Button, Col, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row} from 'reactstrap';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {connect} from 'react-redux';
import {IRootState} from 'app/shared/reducers';
import {AvForm, AvGroup, AvInput, AvRadio, AvRadioGroup} from 'availity-reactstrap-validation';
import {IJourney} from 'app/shared/model/journey.model';
import {
  createEntity as createIncidentReport,
  getEntities as getIncidentReports,
  updateEntity as updateIncidentReport
} from 'app/entities/incident-report/incident-report.reducer';

interface IncidentReportModalProps extends StateProps, DispatchProps {
  isOpen: boolean;
  toggle: () => void;
  journey: IJourney;
  isCancel?: boolean; // true for cancel journey, false for report incident
  onSave: (incidentId: string, incidentDescription: string, isCancel: boolean) => void;
}

const IncidentReportModalComponent = (props: IncidentReportModalProps) => {
  const {
    isOpen,
    toggle,
    journey,
    isCancel = false,
    incidentReports,
    loading,
    updating,
    onSave
  } = props;

  const [selectedIncidentId, setSelectedIncidentId] = useState<string>('');
  const [incidentDescription, setIncidentDescription] = useState<string>('');
  const [isOtherSelected, setIsOtherSelected] = useState<boolean>(false);
  const [filteredIncidentReports, setFilteredIncidentReports] = useState<any[]>([]);

  const patientCountryId = journey?.patientCase?.patient?.countryId;

  // Load incident reports when modal opens
  useEffect(() => {
    if (isOpen && (!incidentReports || incidentReports.length === 0)) {
      props.getIncidentReports();
    }
  }, [isOpen]);

  // Filter incident reports based on patient country
  useEffect(() => {
    if (incidentReports && incidentReports.length > 0 && patientCountryId) {
      setFilteredIncidentReports(incidentReports?.filter(
        report => report.countryId === patientCountryId && report.isActive
      ).sort((a, b) => a.displayOrder - b.displayOrder) || []);
    }
  }, [incidentReports, patientCountryId]);


  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      // If journey has existing incident report, populate the form
      if (journey?.incidentReport) {
        setSelectedIncidentId(journey.incidentReport.id);
        setIncidentDescription(journey.incidentReportDescription || '');
        setIsOtherSelected(filteredIncidentReports[filteredIncidentReports.length - 1]?.id === journey.incidentReport.id);
      } else {
        setSelectedIncidentId('');
        setIncidentDescription('');
        setIsOtherSelected(false);
      }
    }
  }, [isOpen, journey, filteredIncidentReports]);

  const handleIncidentSelection = (incidentId: string) => {
    if (!incidentId) {
      return;
    }

    setSelectedIncidentId(incidentId);
    const isOther = filteredIncidentReports[filteredIncidentReports.length - 1]?.id === incidentId;
    setIsOtherSelected(isOther);

    // Clear description if not "other"
    if (!isOther) {
      setIncidentDescription('');
    }
  };

  const handleSubmit = (event: any, values: any) => {
    event.preventDefault();

    if (!selectedIncidentId) {
      alert('Please select an incident type.');
      return;
    }

    if (isOtherSelected && !incidentDescription.trim()) {
      alert('Please provide incident description for "Other" option.');
      return;
    }

    onSave(selectedIncidentId, incidentDescription, isCancel);
  };

  const handleCancel = () => {
    setSelectedIncidentId('');
    setIncidentDescription('');
    setIsOtherSelected(false);
    toggle();
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>
        {isCancel ? 'Cancel Journey - Select Incident' : 'Report Incident'}
      </ModalHeader>
      <ModalBody>
        <AvForm onValidSubmit={handleSubmit}>
          <Row>
            <Col md="12">
              <AvGroup>
                <Label for="incidentType">
                  {isCancel ? 'Please select a reason for cancelling this journey:' : 'Please select the type of incident:'}
                </Label>
                <AvRadioGroup
                  name="incidentType"
                  required
                  value={selectedIncidentId}
                  onChange={(e) => handleIncidentSelection(e?.target?.value || null)}
                  className="mt-3"
                >
                  {loading ? (
                    <div className="text-center py-4">
                      <FontAwesomeIcon icon="spinner" spin/> Loading incident types...
                    </div>
                  ) : filteredIncidentReports.length === 0 ? (
                    <div className="text-center py-4 text-muted">
                      No incident types available for this country.
                    </div>
                  ) : (
                    filteredIncidentReports.map(incident => (
                      <AvRadio
                        key={incident.id}
                        label={incident.enIncident}
                        value={incident.id}
                      />
                    ))
                  )}
                </AvRadioGroup>
              </AvGroup>
            </Col>
          </Row>

          {isOtherSelected && (
            <Row>
              <Col md="12">
                <AvGroup>
                  <Label for="incidentDescription">Incident Details</Label>
                  <AvInput
                    type="textarea"
                    name="incidentDescription"
                    id="incidentDescription"
                    value={incidentDescription}
                    onChange={(e) => setIncidentDescription(e?.target?.value || null)}
                    maxLength={500}
                    placeholder="Please provide details about the incident"
                    rows={4}
                    required
                  />
                  <small className="text-muted">
                    {incidentDescription.length}/500 characters
                  </small>
                </AvGroup>
              </Col>
            </Row>
          )}
        </AvForm>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={handleCancel} disabled={updating}>
          Cancel
        </Button>
        <Button
          color={isCancel ? "danger" : "primary"}
          onClick={(e) => handleSubmit(e, {})}
          disabled={!selectedIncidentId || updating || (isOtherSelected && !incidentDescription.trim())}
        >
          {updating ? (
            <>
              <FontAwesomeIcon icon="spinner" spin className="mr-2"/>
              {isCancel ? 'Cancelling...' : 'Reporting...'}
            </>
          ) : (
            isCancel ? 'Cancel Journey' : 'Report Incident'
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

const mapStateToProps = (storeState: IRootState) => ({
  incidentReports: storeState.incidentReport.entities,
  loading: storeState.incidentReport.loading,
  updating: storeState.journey.updating,
});

const mapDispatchToProps = {
  getIncidentReports,
  createIncidentReport,
  updateIncidentReport,
};

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = typeof mapDispatchToProps;

const IncidentReportModal = connect(mapStateToProps, mapDispatchToProps)(IncidentReportModalComponent);

export default IncidentReportModal;
